import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  IconButton,
  Collapse,
  AccordionSummary,
  AccordionDetails,
  CardContent,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TablePagination,
  TableSortLabel,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  CircularProgress,
  Tooltip,
  Snackbar,
  Alert,
  Backdrop,
  Checkbox
} from "@mui/material";
import * as XLSX from 'xlsx';
import {
  KeyboardArrowDown as KeyboardArrowDownIcon,
  ExpandMore as ExpandMoreIcon,
  Search as SearchIcon,
  Delete as DeleteIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  FileDownload as FileDownloadIcon,
} from "@mui/icons-material";
import SearchBar from "../SearchBar";
import useFetchWithMsal from "../../hooks/useFetchWithMsal";
import { protectedResources } from "../../authConfig";
import translations from "./translations";
import { softColors } from "../../constants/colors";
import { ObjectPopover } from "./ObjectPopover";
import VerificationApproval from "./VerificationApproval";
import { DetailRow } from "./DetailRow";
import {
  StyledContainer,
  StyledCard,
  StyledTableContainer,
  StyledTableRow,
  StyledTableCell,
  StyledTableHeadCell,
  StyledAccordion,
  StyledDetailCard,
  StyledChip,
} from "./styles";
import DocumentViewerButton from "./DocumentViewerButton";
import axios from 'axios';
import { toast } from 'react-toastify';
import VisibilityIcon from '@mui/icons-material/Visibility';
import RefreshIcon from '@mui/icons-material/Refresh';

// Dynamic verification display filter - returns true if verification should be displayed
const shouldDisplayVerification = (key, verification, invoice) => {
  // Always show verification if it exists (even with only manuallyApproved field)
  if (!verification || typeof verification !== 'object') {
    return false;
  }

  // Special case for consolidated invoices - skip valueDifference
  const isConsolidated = invoice.InvoiceNumber?.includes('-');
  if (isConsolidated && key === "valueDifference") {
    return false;
  }
  if (isConsolidated && key === "DollarToShekelRateVerification") {
    return false;
  }

  // Skip internal verification checks not meant for display
  if (key === "exchange_rate_recheck") {
    return false;
  }

  return true;
};

// Get all verifications that should be displayed for this invoice
const getDisplayableVerifications = (invoice) => {
  if (!invoice?.Verifications) return [];

  return Object.entries(invoice.Verifications)
    .filter(([key, verification]) => shouldDisplayVerification(key, verification, invoice))
    .map(([key, verification]) => ({ key, verification }));
};

// Count verifications - simply count the ones that would be displayed
const getVerificationCount = (verifications, invoice) => {
  if (!verifications) return 0;
  return getDisplayableVerifications(invoice).length;
};

// Count successful verifications
const getSuccessVerificationCount = (verifications, invoice) => {
  if (!verifications) return 0;

  return getDisplayableVerifications(invoice)
    .filter(({ key, verification }) => {
      // Count as success if any of these conditions are true
      return verification.Result === true ||
        verification.Passed === true ||
        verification.manuallyApproved === true;
    }).length;
};

const getVerificationStatus = (verifications, invoice) => {
  if (!verifications) return "error";

  const totalCount = getVerificationCount(verifications, invoice);
  const successCount = getSuccessVerificationCount(verifications, invoice);

  return totalCount > 0 && successCount === totalCount ? "success" : "error";
};

// Helper Components
// Add a recursion depth limit to prevent stack overflow
const formatValue = (value, invoice, depth = 0) => {
  // Prevent infinite recursion by limiting depth
  if (depth > 3) {
    return "[Complex Object]";
  }

  // בדיקה אם זו תוצאת בדיקת מחירי שילוח מול מחירון
  if (
    value &&
    typeof value === "object" &&
    value.success !== undefined &&
    value.freight &&
    value.fuel &&
    value.priceListInfo
  ) {
    return formatShippingPriceValidation(value);
  }

  if (value === null || value === undefined) return "-";

  if (typeof value === "object") {
    if (value instanceof Date) {
      // פורמט תאריך עברי סטנדרטי
      return new Intl.DateTimeFormat('he-IL', { day: '2-digit', month: '2-digit', year: 'numeric' }).format(value);
    }

    if (Array.isArray(value)) {
      // מעצב כל פריט במערך ומחבר אותם עם פסיק - increase depth
      return value.map(item => formatValue(item, invoice, depth + 1)).join(", ");
    }

    // Detect circular references by checking if the object contains itself
    try {
      JSON.stringify(value);
    } catch (err) {
      if (err.message.includes('circular')) {
        return "[Circular Reference]";
      }
    }

    // טיפול באובייקטי עלויות עם שמות שדות בעברית
    if (value.hasOwnProperty("סכום") && value.hasOwnProperty("סכום במט\"ח")) {
      const amountILS = Number.isInteger(value["סכום"]) ? value["סכום"] : Number(value["סכום"]).toFixed(2);
      const foreignAmount = Number.isInteger(value["סכום במט\"ח"]) ? value["סכום במט\"ח"] : Number(value["סכום במט\"ח"]).toFixed(2);
      let displayString = `${translations.amount || 'סכום'}: ${amountILS}, ${translations.forexAmount || 'סכום מט"ח'}: ${foreignAmount}`;
      if (value["מטבע"]) {
        displayString += ` ${value["מטבע"]}`;
      }
      return displayString;
    }

    // טיפול באובייקטי עלויות עם שמות שדות באנגלית
    if (value.hasOwnProperty("amount") && value.hasOwnProperty("forexAmount")) {
      const amountVal = Number.isInteger(value.amount) ? value.amount : Number(value.amount).toFixed(2);
      const forexAmountVal = Number.isInteger(value.forexAmount) ? value.forexAmount : Number(value.forexAmount).toFixed(2);

      const amountLabel = translations.amount || "סכום";
      const forexAmountLabel = translations.forexAmount || 'סכום מט"ח';

      let displayString = `${amountLabel}: ${amountVal}, ${forexAmountLabel}: ${forexAmountVal}`;
      if (value.currency) {
        displayString += ` ${value.currency}`;
      }
      return displayString;
    }

    // Fallback עבור אובייקטים אחרים - increase depth for recursive calls
    try {
      return Object.entries(value)
        .map(([key, val]) => {
          const translatedKey = translations[key] || key;
          // Increase depth for recursive calls
          return `${translatedKey}: ${formatValue(val, invoice, depth + 1)}`;
        })
        .join(", ");
    } catch (error) {
      console.error("Error formatting object value:", error);
      return "[Complex Object]";
    }
  }

  if (typeof value === "number") {
    return Number.isInteger(value) ? value : value.toFixed(2);
  }

  // Handle all other types safely by converting to string
  try {
    return String(value);
  } catch (error) {
    return "[Error]";
  }
};

const AlertStatus = ({ statusMessage, statusType, awbNumber }) => {
  // קביעת סוג ההתראה לפי סוג הסטטוס
  let severity = "info";
  if (statusType === "WARNING") severity = "warning";
  if (statusType === "ERROR") severity = "error";
  if (statusType === "SUCCESS") severity = "success";

  return (
    <Box sx={{ p: 3, mb: 3 }}>
      <Box sx={{
        display: 'flex',
        flexDirection: 'column',
        p: 2,
        borderRadius: 1,
        bgcolor: severity === 'warning' ? '#fff4e5' :
          severity === 'error' ? '#ffebee' :
            severity === 'success' ? '#e8f5e9' : '#e3f2fd',
        border: '1px solid',
        borderColor: severity === 'warning' ? '#ffcc80' :
          severity === 'error' ? '#ef9a9a' :
            severity === 'success' ? '#a5d6a7' : '#90caf9',
      }}>
        <Typography
          variant="subtitle1"
          sx={{
            fontWeight: 600, mb: 1, color: severity === 'warning' ? '#f57c00' :
              severity === 'error' ? '#d32f2f' :
                severity === 'success' ? '#388e3c' : '#1976d2'
          }}
        >
          הודעת מערכת
        </Typography>
        <Typography variant="body1">{statusMessage}</Typography>
        {awbNumber && (
          <Typography variant="body2" sx={{ mt: 1 }}>
            מספר שטר מטען: {awbNumber}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

// פונקציית עזר לבדיקה אם המשלוח הוא מסוג P/P
const isPrePaidShipment = (invoice) => {
  return (invoice?.InvoiceProperties?.paymentMethod === 'P/P' ||
    invoice?.paymentMethod === 'P/P' ||
    invoice?.Weight?.paymentMethod === 'P/P');
};

// פונקציה להצגת תוצאות בדיקת מחירון
const formatShippingPriceValidation = (validation) => {
  if (!validation.success) {
    return (
      <Box sx={{ p: 2, border: '1px solid #f44336', borderRadius: 1, bgcolor: '#ffebee' }}>
        <Typography variant="body2" color="error">
          ❌ {validation.message}
        </Typography>
      </Box>
    );
  }

  const { freight, fuel, weightInfo, zone, country, priceListInfo } = validation;

  return (
    <Box sx={{ p: 2, border: '1px solid #4caf50', borderRadius: 1, bgcolor: '#e8f5e8' }}>
      <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
        ✅ בדיקת מחירון - אזור {zone} ({country})
      </Typography>

      <Typography variant="caption" sx={{ display: 'block', mb: 1, color: 'text.secondary' }}>
        מחירון: {priceListInfo.nameFile}
      </Typography>

      <Box sx={{ mb: 1 }}>
        <Typography variant="caption" sx={{ fontWeight: 'bold' }}>משקלים:</Typography>
        <Typography variant="caption" sx={{ display: 'block' }}>
          חשבונית: {weightInfo.invoiceWeight}ק"ג,
          שילוח: {weightInfo.shippingWeight}ק"ג (מעוגל: {weightInfo.shippingRoundedWeight}ק"ג)
        </Typography>
      </Box>

      <Box sx={{ mb: 1 }}>
        <Typography variant="caption" sx={{ fontWeight: 'bold' }}>הובלה:</Typography>
        <Box sx={{ pl: 1 }}>
          <Typography variant="caption" sx={{ display: 'block' }}>
            חשבונית: ${freight.invoice}
            {freight.matchInvoicePriceList ? ' ✅' : ` ❌ (מחירון: $${freight.priceList}, הפרש: $${freight.differenceInvoicePriceList.toFixed(2)})`}
          </Typography>
          <Typography variant="caption" sx={{ display: 'block' }}>
            מסמכי שילוח: ${freight.shippingDocs}
            {freight.matchInvoiceShipping ? ' ✅' : ' ❌'} מול חשבונית
          </Typography>
        </Box>
      </Box>

      <Box>
        <Typography variant="caption" sx={{ fontWeight: 'bold' }}>דלק:</Typography>
        <Box sx={{ pl: 1 }}>
          <Typography variant="caption" sx={{ display: 'block' }}>
            חשבונית: ${fuel.invoice}
            {fuel.matchInvoicePriceList ? ' ✅' : ` ❌ (מחירון: $${fuel.priceList}, הפרש: $${fuel.differenceInvoicePriceList.toFixed(2)})`}
          </Typography>
          <Typography variant="caption" sx={{ display: 'block' }}>
            מסמכי שילוח: ${fuel.shippingDocs}
            {fuel.matchInvoiceShipping ? ' ✅' : ' ❌'} מול חשבונית
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

const CheckedInvoicesTable = () => {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [orderBy, setOrderBy] = useState("Date");
  const [order, setOrder] = useState("desc");
  const [filter, setFilter] = useState("");
  const [expandedRow, setExpandedRow] = useState(null);

  const { execute } = useFetchWithMsal({
    scopes: protectedResources.apiScopes.scopes.read,
  });

  const visibleInvoiceFields = [
    "ArrivalDate",
    "Countryoforigin",
    "Weight",
    "TransportationCost",
    "FuelCost",
    "exchangeRateUSDToILS",
    "importFileNumber", // הוספת מספר תיק יבוא לרשימת השדות המוצגים
  ];

  // הוספת משתני סטייט לטיפול בדיאלוג אישור ובתהליך סריקה חוזרת
  const [recheckDialogOpen, setRecheckDialogOpen] = useState(false);
  const [currentRecheckInvoice, setCurrentRecheckInvoice] = useState(null);
  const [recheckingInProgress, setRecheckingInProgress] = useState(false);
  const [recheckingResult, setRecheckingResult] = useState({ open: false, success: false, message: '' });
  const [recentlyRechecked, setRecentlyRechecked] = useState(new Set());

  // State לניהול בחירות לייצוא Excel
  const [selectedForExport, setSelectedForExport] = useState(new Set());
  const [selectAllForExport, setSelectAllForExport] = useState(false);
  const [showExportMode, setShowExportMode] = useState(false);

  // פונקציית עזר משופרת לחילוץ מאפיין מחשבונית
  const getInvoiceProperty = (invoice, property, defaultValue = "-") => {
    // מקרים מיוחדים לשדות ספציפיים
    if (property === "ServiceProviderName") {
      if (invoice.InvoiceProperties?.issuer?.name) {
        return invoice.InvoiceProperties.issuer.name;
      }
      if (invoice.ServiceProviderName) {
        return invoice.ServiceProviderName;
      }
      if (invoice.issuer) {
        return typeof invoice.issuer === 'object' ? invoice.issuer.name : invoice.issuer;
      }
      return "O.P.S.I";
    }

    if (property === "importFileNumber") {
      // First check if importFileNumber exists directly on the invoice
      if ('importFileNumber' in invoice) {
        const value = invoice.importFileNumber;
        if (value !== null && value !== undefined && value !== '') {
          return value;
        }
      }
      
      // Check in InvoiceProperties
      if (invoice.InvoiceProperties && 'importFileNumber' in invoice.InvoiceProperties) {
        const value = invoice.InvoiceProperties.importFileNumber;
        if (value !== null && value !== undefined && value !== '') {
          return value;
        }
      }
      
      // Try alternative fields
      const alternativeFields = ['customsFileNumber', 'IMPFNUM', 'CUSTOMSFILENO'];
      for (const field of alternativeFields) {
        if (invoice[field]) {
          return invoice[field];
        }
        if (invoice.InvoiceProperties && invoice.InvoiceProperties[field]) {
          return invoice.InvoiceProperties[field];
        }
      }
      
      return defaultValue;
    }

    if (property === "awbNumber") {
      if (invoice.awbNumber) {
        return invoice.awbNumber;
      }
      if (invoice.Verifications?.invoiceMatchesShipping?.AWBNumber) {
        return invoice.Verifications.invoiceMatchesShipping.AWBNumber;
      }
      if (invoice.Verifications?.invoiceMatchesShipping?.InvoiceBillOfLadingNumber) {
        return invoice.Verifications.invoiceMatchesShipping.InvoiceBillOfLadingNumber;
      }
      if (invoice.awbNumbers && Array.isArray(invoice.awbNumbers) && invoice.awbNumbers.length > 0) {
        return invoice.awbNumbers[0];
      }
    }

    if (property === "paymentMethod") {
      if (invoice.InvoiceProperties?.paymentMethod) {
        return invoice.InvoiceProperties.paymentMethod;
      }
      if (invoice.paymentMethod) {
        return invoice.paymentMethod;
      }
      if (invoice.Weight?.paymentMethod) {
        return invoice.Weight.paymentMethod;
      }
      return defaultValue;
    }

    if (property === "Countryoforigin") {
      // תחילה בדוק ישירות באובייקט (כמו שנשמר במונגו)
      if (invoice.shipper?.Countryoforigin) {
        return invoice.shipper.Countryoforigin;
      }
      // אחר כך בדוק ב-InvoiceProperties
      if (invoice.InvoiceProperties?.shipper?.Countryoforigin) {
        return invoice.InvoiceProperties.shipper.Countryoforigin;
      }
      // ולבסוף בדוק ב-Verifications
      if (invoice.Verifications?.originCountry?.InvoiceOriginCountry) {
        return invoice.Verifications.originCountry.InvoiceOriginCountry;
      }
    }

    // Enhance the Weight property display to show a warning for high weights
    if (property === "Weight") {
      // תיקון: בדוק אם יש אובייקט React מורכב ומנע הצגה שלו כ-string
      try {
        // אם יש סימון משקל גבוה, הוסף אינדיקציה בולטת לכך
        if (invoice.isHighWeight === true) {
          // קבל את הערך המספרי של המשקל בצורה בטוחה
          const weightValue = invoice.weightAmount || 
                             invoice.Weight?.value || 
                             invoice.InvoiceProperties?.Weight?.value || 
                             invoice.backendWeight || 
                             0;
          
          const weightUnit = invoice.Weight?.unit || 
                            invoice.InvoiceProperties?.Weight?.unit || 
                            'kg';
          
          // הוספת סימון מודגש יותר למשקל גבוה - כקומפוננטה פשוטה
          return (
            <Box component="span" sx={{ 
              display: 'flex', 
              alignItems: 'center',
              backgroundColor: '#fff8e1',
              border: '1px solid #ffe082',
              borderRadius: '4px',
              px: 1,
              py: 0.5,
              fontWeight: 'bold'
            }}>
              <Typography 
                component="span" 
                sx={{ 
                  fontWeight: 'bold',
                  color: '#f57c00',
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                ⚠️ {weightValue} {weightUnit} - משקל גבוה
              </Typography>
            </Box>
          );
        }
        
        // טיפול בשדות משקל רגילים בצורה בטוחה
        if (invoice.InvoiceProperties?.Weight?.value) {
          return `${invoice.InvoiceProperties.Weight.value} ${invoice.InvoiceProperties.Weight.unit || 'kg'}`;
        }
        if (invoice.Verifications?.weightDifference?.InvoiceWeight) {
          return `${invoice.Verifications.weightDifference.InvoiceWeight} kg`;
        }
        if (invoice.Weight?.value) {
          return `${invoice.Weight.value} ${invoice.Weight.unit || 'kg'}`;
        }
        if (invoice.backendWeight) {
          return `${invoice.backendWeight} kg`;
        }
        if (invoice.weightAmount) {
          return `${invoice.weightAmount} ${invoice.weightUnit || 'kg'}`;
        }
      } catch (error) {
        console.error("Error formatting weight:", error);
        // במקרה של שגיאה, החזר רק את המשקל המספרי
        const fallbackWeight = invoice.weightAmount || 
                              invoice.backendWeight || 
                              invoice.Weight?.value || 
                              invoice.InvoiceProperties?.Weight?.value;
        return fallbackWeight ? `${fallbackWeight} kg` : defaultValue;
      }
    }

    // במקרה שאין מקרה מיוחד, חפש את הערך בצורה רגילה
    if (invoice.InvoiceProperties && invoice.InvoiceProperties[property] !== undefined) {
      return invoice.InvoiceProperties[property];
    }

    if (invoice[property] !== undefined) {
      return invoice[property];
    }

    return defaultValue;
  };
  
  const fetchInvoices = async () => {
    try {
      console.log(
        "${process.env.REACT_APP_BACKEND_BASE_URL}/api/checkInvoices/getCheckedInvoices",
        `${process.env.REACT_APP_BACKEND_BASE_URL}/api/checkInvoices/getCheckedInvoices`
      );

      const response = await execute(
        "GET",
        `${process.env.REACT_APP_BACKEND_BASE_URL}/api/checkInvoices/getCheckedInvoices`
      );
      console.log("Invoice data:", response.data);

      // מיון ברירת מחדל: החשבוניות החדשות ביותר ראשונות (לפי timestamp או todayDate)
      const sortedData = response.data.sort((a, b) => {
        const dateA = new Date(a.timestamp || a.todayDate || a.createdAt || 0);
        const dateB = new Date(b.timestamp || b.todayDate || b.createdAt || 0);
        return dateB - dateA; // סדר יורד - החדשות ביותר ראשונות
      });

      setInvoices(sortedData);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching invoices:", error);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInvoices();
  }, [execute]);

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleDeleteInvoice = async (invoiceId) => {
    try {
      console.log("invoiceId", invoiceId);

      await execute(
        "POST",
        `${process.env.REACT_APP_BACKEND_BASE_URL}/api/checkInvoices/deleteCheckedInvoice`,
        { invoiceId }
      );

      setInvoices(invoices.filter((invoice) => invoice._id !== invoiceId));
    } catch (error) {
      console.error("Error deleting invoice:", error);
    }
  };

  // פונקציה משופרת לפתיחת דיאלוג אישור לפני ביצוע סריקה חוזרת
  const openRecheckDialog = (invoice) => {
    setCurrentRecheckInvoice(invoice);
    setRecheckDialogOpen(true);
  };

  // פונקציה לסגירת דיאלוג האישור
  const closeRecheckDialog = () => {
    setRecheckDialogOpen(false);
    setCurrentRecheckInvoice(null);
  };

  // פונקציה משופרת לביצוע הסריקה החוזרת
  const handleRecheckInvoice = async () => {
    if (!currentRecheckInvoice) return;

    const invoiceId = currentRecheckInvoice._id;

    try {
      setRecheckDialogOpen(false);
      setRecheckingInProgress(true);

      const response = await execute(
        "POST",
        `${process.env.REACT_APP_BACKEND_BASE_URL}/api/checkInvoices/rescan`,
        { invoiceId }
      );

      if (!response || response.success === false) {
        throw new Error(
          response?.message ||
          response?.error ||
          'שגיאה לא ידועה בבדיקה החוזרת'
        );
      }

      console.log('סריקה חוזרת הושלמה בהצלחה:', response);

      setRecentlyRechecked(prev => new Set(prev).add(invoiceId));

      // רענון הנתונים מהשרת
      await fetchInvoices();

      setRecheckingResult({
        open: true,
        success: true,
        message: 'החשבונית נבדקה מחדש בהצלחה'
      });

      // רענון העמוד אחרי 1.5 שניות כדי שהמשתמשת תראה את ההודעה
      setTimeout(() => {
        window.location.reload();
      }, 1500);

    } catch (error) {
      console.error('Error rechecking invoice:', error);

      let errorMessage = 'שגיאה לא ידועה בבדיקה החוזרת';

      if (error.response) {
        errorMessage = error.response.data?.message || error.response.data?.error || error.message;
      } else if (error.status) {
        errorMessage = error.msg || error.message || errorMessage;
      } else if (error.message) {
        errorMessage = error.message;
      }

      setRecheckingResult({
        open: true,
        success: false,
        message: `שגיאה בבדיקה החוזרת: ${errorMessage}`
      });

    } finally {
      setRecheckingInProgress(false);
      setCurrentRecheckInvoice(null);
    }
  };

  // סגירת הודעת תוצאה
  const handleCloseRecheckingResult = () => {
    setRecheckingResult({ ...recheckingResult, open: false });
  };

  // פונקציות לניהול בחירת שורות לייצוא
  const handleSelectForExport = (invoiceId) => {
    setSelectedForExport(prev => {
      const newSet = new Set(prev);
      if (newSet.has(invoiceId)) {
        newSet.delete(invoiceId);
      } else {
        newSet.add(invoiceId);
      }
      return newSet;
    });
  };

  const handleSelectAllForExport = () => {
    if (selectAllForExport) {
      setSelectedForExport(new Set());
      setSelectAllForExport(false);
    } else {
      const allIds = new Set(paginatedInvoices.map(inv => inv._id));
      setSelectedForExport(allIds);
      setSelectAllForExport(true);
    }
  };

  // פונקציה לייצוא לאקסל
  const handleExportToExcel = () => {
    // אם לא במצב בחירה - כנס למצב בחירה
    if (!showExportMode) {
      setShowExportMode(true);
      return;
    }

    // אם במצב בחירה - בצע ייצוא
    if (selectedForExport.size === 0) {
      alert('נא לבחור לפחות חשבונית אחת לייצוא');
      return;
    }

    // סינון החשבוניות שנבחרו
    const selectedInvoices = invoices.filter(inv => selectedForExport.has(inv._id));

    // יצירת נתונים לאקסל
    const excelData = selectedInvoices.map((invoice, index) => ({
      'מס\' סידורי': index + 1,
      'ספק': invoice.issuer?.name || invoice.ServiceProviderName || invoice.InvoiceProperties?.issuer?.name || '-',
      'מספר שט"מ (AWB)': invoice.awbNumber || invoice.awbNumbers?.[0] || '-',
      'מספר תיק יבוא': invoice.importFileNumber || invoice.InvoiceProperties?.importFileNumber || '-',
      'מספר חשבונית': invoice.InvoiceNumber || invoice.InvoiceProperties?.InvoiceNumber || '-',
      'תאריך חשבונית': invoice.InvoiceDate || invoice.InvoiceProperties?.InvoiceDate || '-',
      'שיטת תשלום': invoice.paymentMethod || invoice.InvoiceProperties?.paymentMethod || invoice.Weight?.paymentMethod || '-',
      'תאריך בדיקה': invoice.todayDate || '-',
    }));

    // יצירת workbook ו-worksheet
    const ws = XLSX.utils.json_to_sheet(excelData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'חשבוניות');

    // הגדרת רוחב עמודות
    const colWidths = [
      { wch: 10 },  // מס' סידורי
      { wch: 15 },  // ספק
      { wch: 20 },  // AWB
      { wch: 20 },  // תיק יבוא
      { wch: 20 },  // מספר חשבונית
      { wch: 15 },  // תאריך חשבונית
      { wch: 15 },  // שיטת תשלום
      { wch: 15 },  // תאריך בדיקה
    ];
    ws['!cols'] = colWidths;

    // יצירת שם קובץ עם תאריך
    const today = new Date();
    const dateStr = `${today.getDate()}-${today.getMonth() + 1}-${today.getFullYear()}`;
    const fileName = `חשבוניות_${dateStr}.xlsx`;

    // שמירת הקובץ
    XLSX.writeFile(wb, fileName);

    // איפוס הבחירות והסתרת מצב ייצוא
    setSelectedForExport(new Set());
    setSelectAllForExport(false);
    setShowExportMode(false);

    // הצגת הודעת הצלחה
    setRecheckingResult({
      open: true,
      success: true,
      message: `יוצאו ${selectedForExport.size} חשבוניות לקובץ Excel בהצלחה!`
    });
  };

  const filteredInvoices = invoices.filter((invoice) => {
    if (!filter) return true;

    const searchFields = [];

    if (invoice.InvoiceProperties) {
      try {
        Object.values(invoice.InvoiceProperties).forEach(value => {
          if (value !== null && value !== undefined) {
            searchFields.push(String(value).toLowerCase());
          }
        });
      } catch (error) {
        console.error("Error processing InvoiceProperties:", error);
      }
    }

    const directFields = [
      'ServiceProviderName', 'InvoiceDate', 'InvoiceNumber',
      'awbNumber', 'paymentMethod', 'todayDate'
    ];

    directFields.forEach(field => {
      if (invoice[field] !== null && invoice[field] !== undefined) {
        searchFields.push(String(invoice[field]).toLowerCase());
      }
    });

    if (invoice.Verifications) {
      for (const verKey in invoice.Verifications) {
        const verification = invoice.Verifications[verKey];
        if (verification && typeof verification === 'object') {
          for (const vField in verification) {
            if (verification[vField] !== null && verification[vField] !== undefined) {
              searchFields.push(String(verification[vField]).toLowerCase());
            }
          }
        }
      }
    }

    if (invoice.awbNumbers && Array.isArray(invoice.awbNumbers)) {
      invoice.awbNumbers.forEach(num => {
        if (num !== null && num !== undefined) {
          searchFields.push(String(num).toLowerCase());
        }
      });
    }

    return searchFields.some(text => text.includes(filter.toLowerCase()));
  });

  const sortedInvoices = filteredInvoices.sort((a, b) => {
    const aValue = getInvoiceProperty(a, orderBy);
    const bValue = getInvoiceProperty(b, orderBy);

    if (aValue === "-" && bValue === "-") return 0;
    if (aValue === "-") return 1;
    if (bValue === "-") return -1;

    const isAsc = order === "asc";

    if (aValue < bValue) return isAsc ? -1 : 1;
    if (aValue > bValue) return isAsc ? 1 : -1;
    return 0;
  });

  const paginatedInvoices = sortedInvoices.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <Typography variant="h6" color="textSecondary">
          טוען נתונים...
        </Typography>
      </Box>
    );
  }

  return (
    <StyledContainer>
      <Box
        sx={{
          width: "100%",
          px: { xs: 2, sm: 3, md: 4 },
          maxWidth: "100%",
        }}
      >
        <Box mb={4} sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <Box sx={{ flex: 1, minWidth: '300px' }}>
            <SearchBar searchTerm={filter} setSearchTerm={setFilter} />
          </Box>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            {showExportMode && selectedForExport.size > 0 && (
              <Typography variant="body2" color="primary" sx={{ fontWeight: 'bold' }}>
                {selectedForExport.size} נבחרו
              </Typography>
            )}
            <Button
              variant="contained"
              startIcon={<FileDownloadIcon />}
              onClick={handleExportToExcel}
              sx={{
                bgcolor: showExportMode && selectedForExport.size > 0 ? softColors.success : softColors.primary,
                '&:hover': {
                  bgcolor: showExportMode && selectedForExport.size > 0 ? softColors.successDark : softColors.primaryDark,
                },
                color: 'white',
                fontWeight: 'bold'
              }}
            >
              {showExportMode ? (selectedForExport.size > 0 ? 'ייצא לאקסל' : 'בחר חשבוניות') : 'ייצוא לאקסל'}
            </Button>
            {showExportMode && (
              <Button
                variant="outlined"
                size="small"
                onClick={() => {
                  setSelectedForExport(new Set());
                  setSelectAllForExport(false);
                  setShowExportMode(false);
                }}
                sx={{ color: 'error.main', borderColor: 'error.main' }}
              >
                ביטול
              </Button>
            )}
          </Box>
        </Box>
        <StyledCard>
          <StyledTableContainer>
            <Table sx={{ width: "100%", minWidth: "100%" }}>
              <TableHead>
                <TableRow>
                  <StyledTableHeadCell padding="checkbox">
                    {showExportMode && (
                      <Checkbox
                        checked={selectAllForExport}
                        onChange={handleSelectAllForExport}
                        sx={{
                          color: softColors.primary,
                          '&.Mui-checked': {
                            color: softColors.primary,
                          },
                        }}
                      />
                    )}
                  </StyledTableHeadCell>
                  {[
                    "ServiceProviderName",
                    "InvoiceDate",
                    "InvoiceNumber",
                    "awbNumber",
                    "importFileNumber", // הוספת עמודת מספר תיק יבוא
                    "paymentMethod",
                    "checkDate",
                  ].map((headCell) => (
                    <StyledTableHeadCell key={headCell}>
                      <TableSortLabel
                        active={orderBy === headCell}
                        direction={orderBy === headCell ? order : "asc"}
                        onClick={() => handleRequestSort(headCell)}
                        sx={{
                          color: "inherit",
                          "&.Mui-active": {
                            color: softColors.primary,
                          },
                        }}
                      >
                        {headCell === "importFileNumber" ? 
                          "מספר תיק יבוא" : 
                          (translations[headCell] || headCell)}
                      </TableSortLabel>
                    </StyledTableHeadCell>
                  ))}
                  <StyledTableHeadCell align="center">
                    סטטוס
                  </StyledTableHeadCell>
                  <StyledTableHeadCell align="right"></StyledTableHeadCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedInvoices.map((invoice, index) => {
                  const wasRecentlyRechecked = recentlyRechecked.has(invoice._id);
                  // סימון אם יש משקל גבוה
                  const hasHighWeight = invoice.isHighWeight;

                  return (
                    <React.Fragment key={index}>
                      <StyledTableRow
                        sx={{
                          ...(wasRecentlyRechecked && {
                            backgroundColor: 'rgba(76, 175, 80, 0.05)',
                          }),
                          ...(hasHighWeight && {
                            backgroundColor: 'rgba(255, 193, 7, 0.15)', // Increased visibility
                            borderLeft: '4px solid #ff9800', // Thicker orange border
                            position: 'relative',
                            '&:hover': {
                              backgroundColor: 'rgba(255, 193, 7, 0.25)',
                            }
                          })
                        }}
                      >
                        <StyledTableCell padding="checkbox">
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            {showExportMode && (
                              <Checkbox
                                checked={selectedForExport.has(invoice._id)}
                                onChange={() => handleSelectForExport(invoice._id)}
                                sx={{
                                  color: softColors.primary,
                                  '&.Mui-checked': {
                                    color: softColors.primary,
                                  },
                                  padding: '4px',
                                }}
                              />
                            )}
                            <IconButton
                              size="small"
                              onClick={() =>
                                setExpandedRow(expandedRow === index ? null : index)
                              }
                              sx={{
                                transition: "transform 0.2s",
                                transform:
                                  expandedRow === index ? "rotate(180deg)" : "none",
                              }}
                            >
                              <KeyboardArrowDownIcon />
                            </IconButton>
                          </Box>
                        </StyledTableCell>
                        <StyledTableCell>
                          {getInvoiceProperty(invoice, "ServiceProviderName")}
                        </StyledTableCell>
                        <StyledTableCell>
                          {getInvoiceProperty(invoice, "InvoiceDate")}
                        </StyledTableCell>
                        <StyledTableCell>
                          {getInvoiceProperty(invoice, "InvoiceNumber")}
                        </StyledTableCell>
                        <StyledTableCell>
                          {getInvoiceProperty(invoice, "awbNumber")}
                        </StyledTableCell>
                        <StyledTableCell>
                          {invoice.importFileNumber !== null && invoice.importFileNumber !== undefined 
                            ? invoice.importFileNumber 
                            : "-"}
                        </StyledTableCell>
                        <StyledTableCell>
                          {getInvoiceProperty(invoice, "paymentMethod")}
                        </StyledTableCell>
                        <StyledTableCell>
                          {invoice.todayDate ||
                            (invoice.timestamp ? new Date(invoice.timestamp).toLocaleDateString() :
                              (invoice.processingStatus?.timestamp ? new Date(invoice.processingStatus.timestamp).toLocaleDateString() : "-"))}
                        </StyledTableCell>
                        <StyledTableCell align="center">
                          <Box display="flex" alignItems="center" justifyContent="center" gap={1}>
                            {invoice.statusMessage ? (
                              <StyledChip
                                label={invoice.statusMessage}
                                status={invoice.statusType || "warning"}
                                sx={{
                                  backgroundColor: invoice.statusType === 'ERROR' ? '#ffebee' :
                                    invoice.statusType === 'WARNING' ? '#fff4e5' :
                                      invoice.statusType === 'SUCCESS' ? '#e8f5e9' : '#e3f2fd',
                                  color: invoice.statusType === 'ERROR' ? '#d32f2f' :
                                    invoice.statusType === 'WARNING' ? '#f57c00' :
                                      invoice.statusType === 'SUCCESS' ? '#388e3c' : '#1976d2',
                                  border: '1px solid',
                                  borderColor: invoice.statusType === 'ERROR' ? '#ef9a9a' :
                                    invoice.statusType === 'WARNING' ? '#ffcc80' :
                                      invoice.statusType === 'SUCCESS' ? '#a5d6a7' : '#90caf9'
                                }}
                              />
                            ) : (
                              <>
                                <StyledChip
                                  label={`${getSuccessVerificationCount(invoice.Verifications, invoice)}/${getVerificationCount(invoice.Verifications, invoice)}`}
                                  status={getVerificationStatus(invoice.Verifications, invoice) === "success" ? "success" : "error"}
                                />
                                {invoice.Verifications &&
                                  Object.entries(invoice.Verifications).some(([key, v]) =>
                                    v && v.manuallyApproved
                                  ) && (
                                    <StyledChip
                                      label={`${Object.entries(invoice.Verifications)
                                        .filter(([key, v]) => v && v.manuallyApproved)
                                        .length} אישורים ידניים`}
                                      status="manual"
                                      sx={{
                                        backgroundColor: '#e6f4ea',
                                        color: softColors.success,
                                        border: '1px solid #cbe6d3'
                                      }}
                                    />
                                  )}
                                {/* הוספת סימון למשקל גבוה */}
                                {invoice.isHighWeight && (
                                  <StyledChip
                                    label={`משקל גבוה: גדול מ-50 ק"ג`}
                                    status="warning"
                                    sx={{
                                      backgroundColor: '#fff3cd',
                                      color: '#856404',
                                      border: '2px solid #ffeeba',
                                      fontWeight: 'bold',
                                      '& .MuiChip-label': {
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '4px'
                                      }
                                    }}
                                    icon={<span style={{ marginRight: '4px', fontSize: '16px' }}>⚠️</span>}
                                  />
                                )}
                              </>
                            )}
                          </Box>
                        </StyledTableCell>
                        <StyledTableCell align="right">
                          <DocumentViewerButton
                            invoice={{
                              ...invoice,
                              FilePaths: {
                                invoiceUrl: invoice.FilePaths?.invoiceUrl || null,
                                additionalUrls:
                                  invoice.FilePaths?.additionalUrls || [],
                              },
                            }}
                          />
                          <IconButton
                            size="small"
                            onClick={() => handleDeleteInvoice(invoice._id)}
                          >
                            <DeleteIcon />
                          </IconButton>
                          <Tooltip title={`בדיקה חוזרת${wasRecentlyRechecked ? ' (נבדק לאחרונה)' : ''}`}>
                            <IconButton
                              size="small"
                              onClick={() => openRecheckDialog(invoice)}
                              sx={{
                                ...(wasRecentlyRechecked && {
                                  color: 'success.main',
                                  '&:hover': {
                                    backgroundColor: 'rgba(76, 175, 80, 0.1)',
                                  }
                                })
                              }}
                            >
                              <RefreshIcon />
                            </IconButton>
                          </Tooltip>
                        </StyledTableCell>
                      </StyledTableRow>
                      
                      {/* תצוגה מורחבת כאשר מקליקים על שורה */}
                      <TableRow>
                        <TableCell sx={{ py: 0 }} colSpan={9}>
                          <Collapse in={expandedRow === index} timeout="auto">
                            <Box sx={{ p: 3 }}>
                              <StyledDetailCard>
                                <CardContent>
                                  <Typography
                                    variant="h6"
                                    gutterBottom
                                    sx={{
                                      color: softColors.text,
                                      fontWeight: 500,
                                      fontSize: "1rem",
                                    }}
                                  >
                                    פרטי החשבונית
                                  </Typography>

                                  {/* תצוגת מספר תיק יבוא ונתוני משקל גבוה */}
                                  <Box sx={{ mb: 3 }}>
                                    {invoice.importFileNumber && (
                                      <StyledChip
                                        label={`מספר תיק יבוא: ${invoice.importFileNumber}`}
                                        status="info"
                                        sx={{
                                          backgroundColor: '#e3f2fd',
                                          color: '#1976d2',
                                          border: '1px solid #90caf9',
                                          fontSize: '0.875rem',
                                          fontWeight: 500,
                                          mb: 1,
                                          mr: 1
                                        }}
                                      />
                                    )}
                                    
                                    <StyledChip
                                      label={`סוג משלוח: ${isPrePaidShipment(invoice) ? 'P/P' : 'F/C'}`}
                                      status={isPrePaidShipment(invoice) ? "info" : "warning"}
                                      sx={{
                                        backgroundColor: isPrePaidShipment(invoice) ? '#e3f2fd' : '#fff8e1',
                                        color: isPrePaidShipment(invoice) ? '#1976d2' : '#f57c00',
                                        border: '1px solid',
                                        borderColor: isPrePaidShipment(invoice) ? '#90caf9' : '#ffcc80',
                                        fontSize: '0.875rem',
                                        fontWeight: 500,
                                        mb: 1
                                      }}
                                    />
                                    
                                    {invoice.isHighWeight && (
                                      <StyledChip
                                        label={`משקל גבוה: גדול מ-50 ק"ג`}
                                        status="warning"
                                        sx={{
                                          backgroundColor: '#fff3cd',
                                          color: '#856404',
                                          border: '2px solid #ffeeba',
                                          fontWeight: 'bold',
                                          mb: 1,
                                          ml: 1,
                                          '& .MuiChip-label': {
                                            display: 'flex',
                                            alignItems: 'center'
                                          }
                                        }}
                                        icon={<span style={{ marginRight: '4px' }}>⚠️</span>}
                                      />
                                    )}
                                    
                                    <Typography variant="body2" sx={{ color: softColors.textSecondary, mt: 1 }}>
                                      מספר בדיקות שבוצעו: {getVerificationCount(invoice.Verifications, invoice)}
                                    </Typography>
                                  </Box>

                                  <Grid container spacing={3}>
                                    {visibleInvoiceFields.map((field) => (
                                      <Grid item xs={12} sm={6} md={4} key={field}>
                                        <DetailRow
                                          label={field === "importFileNumber" ? "מספר תיק יבוא" : translations[field] || field}
                                          value={field === "Weight" && invoice.isHighWeight ? 
                                            `${invoice.weightAmount || invoice.Weight?.value || invoice.backendWeight || 0} ${invoice.Weight?.unit || 'kg'} (משקל גבוה)` : 
                                            formatValue(getInvoiceProperty(invoice, field))}
                                        />
                                      </Grid>
                                    ))}
                                  </Grid>
                                </CardContent>
                              </StyledDetailCard>

                              <Typography
                                variant="h6"
                                sx={{
                                  mt: 4,
                                  mb: 2,
                                  color: softColors.text,
                                  fontWeight: 500,
                                  fontSize: "1rem",
                                }}
                              >
                                בדיקות שבוצעו ({getVerificationCount(invoice.Verifications, invoice)} בדיקות)
                              </Typography>

                              {invoice.statusMessage ? (
                                <AlertStatus
                                  statusMessage={invoice.statusMessage}
                                  statusType={invoice.statusType || "WARNING"}
                                  awbNumber={invoice.awbNumber || (invoice.awbNumbers?.[0])}
                                />
                              ) : (
                                // הצג את כל הבדיקות שהגיעו מהשרת ללא סינון
                                invoice.Verifications && getDisplayableVerifications(invoice)
                                  .map(({ key, verification }) => (
                                    <StyledAccordion key={key}>
                                      <AccordionSummary
                                        expandIcon={<ExpandMoreIcon sx={{ color: softColors.primary }} />}
                                      >
                                        <Box
                                          display="flex"
                                          alignItems="center"
                                          gap={2}
                                          width="100%"
                                        >
                                          {(verification.Passed === true ||
                                            verification.Result === true ||
                                            verification.manuallyApproved === true) ? (
                                            <CheckCircleIcon
                                              sx={{ color: softColors.success }}
                                            />
                                          ) : (verification.Result === false || verification.Passed === false) ? (
                                            <CancelIcon
                                              sx={{ color: softColors.error }}
                                            />
                                          ) : (
                                            <Box
                                              sx={{
                                                width: 24,
                                                height: 24,
                                                borderRadius: '50%',
                                                backgroundColor: '#fff8e1',
                                                border: '2px solid #ffa726',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                fontSize: '14px',
                                                fontWeight: 'bold',
                                                color: '#f57c00'
                                              }}
                                            >
                                              ?
                                            </Box>
                                          )}
                                          <Box
                                            display="flex"
                                            alignItems="center"
                                            justifyContent="space-between"
                                            width="100%"
                                            marginRight={1}
                                          >
                                            <Typography sx={{ color: softColors.text }}>
                                              {verification.Description || translations[key] || key}
                                            </Typography>
                                            {verification.manuallyApproved && (
                                              <Typography
                                                variant="caption"
                                                sx={{
                                                  color: softColors.success,
                                                  padding: "2px 8px",
                                                  borderRadius: "4px",
                                                  fontWeight: 500,
                                                  marginLeft: 2,
                                                  border: "1px solid #cbe6d3",
                                                }}
                                              >
                                                אושר ידנית
                                              </Typography>
                                            )}
                                          </Box>
                                        </Box>
                                      </AccordionSummary>
                                      <AccordionDetails>
                                        <Table size="small">
                                          <TableBody>
                                            {verification && Object.entries(verification)
                                              .filter(
                                                ([key, value]) =>
                                                  key.toLowerCase() !==
                                                  "description" &&
                                                  key.toLowerCase() !== "result" &&
                                                  ![
                                                    "manuallyapproved",
                                                    "approvedby",
                                                    "approvalnote",
                                                    "approvedat",
                                                  ].includes(key.toLowerCase()) &&
                                                  (key !== "ResultDescription" ||
                                                    (key === "ResultDescription" &&
                                                      value !== ""))
                                              )
                                              .map(([verKey, verValue]) => (
                                                <TableRow key={verKey}>
                                                  <TableCell
                                                    sx={{
                                                      border: "none",
                                                      color:
                                                        softColors.secondaryText,
                                                      width: "40%",
                                                      py: 1.5,
                                                    }}
                                                  >
                                                    {translations[verKey] || verKey}
                                                  </TableCell>
                                                  <TableCell
                                                    sx={{
                                                      border: "none",
                                                      color: softColors.text,
                                                      fontWeight: 500,
                                                      py: 1.5,
                                                    }}
                                                  >
                                                    {typeof verValue === "object" && verValue !== null ?
                                                      formatValue(verValue) : formatValue(verValue)}
                                                  </TableCell>
                                                </TableRow>
                                              ))}
                                          </TableBody>
                                        </Table>
                                        {verification && !verification.Passed && !verification.Result && (
                                          <Box mt={2}>
                                            <VerificationApproval
                                              verification={{
                                                ...verification,
                                                key: key,
                                                manuallyApproved:
                                                  verification.manuallyApproved,
                                              }}
                                              invoiceId={invoice._id}
                                              onVerificationUpdate={(
                                                verificationKey,
                                                updatedVerification
                                              ) => {
                                                setInvoices(
                                                  invoices.map((inv) => {
                                                    if (inv._id === invoice._id) {
                                                      const updatedVerifications = {
                                                        ...inv.Verifications,
                                                      };
                                                      updatedVerifications[
                                                        verificationKey
                                                      ] = updatedVerification;
                                                      return {
                                                        ...inv,
                                                        Verifications:
                                                          updatedVerifications,
                                                      };
                                                    }
                                                    return inv;
                                                  })
                                                );
                                              }}
                                            />
                                          </Box>
                                        )}
                                      </AccordionDetails>
                                    </StyledAccordion>
                                  ))
                              )}
                              {(!invoice.Verifications || Object.keys(invoice.Verifications).length === 0) && !invoice.statusMessage && (
                                <Typography color="textSecondary" align="center" p={3}>
                                  אין בדיקות זמינות לחשבונית זו
                                </Typography>
                              )}
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  )
                })}
              </TableBody>
            </Table>
          </StyledTableContainer>
          <Box
            sx={{
              p: 2,
              borderTop: `1px solid ${softColors.border}`,
              backgroundColor: softColors.background,
            }}
          >
            <TablePagination
              component="div"
              count={filteredInvoices.length}
              page={page}
              onPageChange={(event, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(event) => {
                setRowsPerPage(parseInt(event.target.value, 10));
                setPage(0);
              }}
              labelRowsPerPage="שורות בעמוד"
              labelDisplayedRows={({ from, to, count }) =>
                `${from}-${to} מתוך ${count}`
              }
              sx={{
                color: softColors.text,
                ".MuiTablePagination-select": {
                  paddingTop: 0,
                  paddingBottom: 0,
                },
                ".MuiTablePagination-selectIcon": {
                  top: 0,
                },
              }}
            />
          </Box>
        </StyledCard>
      </Box>

      {/* דיאלוג אישור לסריקה חוזרת */}
      <Dialog
        open={recheckDialogOpen}
        onClose={closeRecheckDialog}
        aria-labelledby="recheck-dialog-title"
        aria-describedby="recheck-dialog-description"
        dir="rtl"
      >
        <DialogTitle id="recheck-dialog-title">
          {"בדיקה חוזרת של חשבונית"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="recheck-dialog-description">
            האם ברצונך לבצע בדיקה חוזרת של חשבונית {currentRecheckInvoice?.InvoiceNumber || ''}?
            <br />
            {currentRecheckInvoice?.awbNumber && <span>מספר שטר מטען: {currentRecheckInvoice.awbNumber}</span>}
            <br /><br />
            בדיקה חוזרת תנסה לשלוף נתונים עדכניים מהמערכת ולבצע אימות מחדש.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeRecheckDialog} color="primary">
            ביטול
          </Button>
          <Button onClick={handleRecheckInvoice} color="primary" variant="contained">
            בצע בדיקה חוזרת
          </Button>
        </DialogActions>
      </Dialog>

      {/* הודעת תוצאה */}
      <Snackbar
        open={recheckingResult.open}
        autoHideDuration={6000}
        onClose={handleCloseRecheckingResult}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={handleCloseRecheckingResult}
          severity={recheckingResult.success ? "success" : "error"}
          sx={{ width: '100%' }}
        >
          {recheckingResult.message}
        </Alert>
      </Snackbar>

      {/* מסך טעינה בזמן בדיקה חוזרת */}
      <Backdrop
        sx={{
          color: '#fff',
          zIndex: (theme) => theme.zIndex.drawer + 1,
          flexDirection: 'column'
        }}
        open={recheckingInProgress}
      >
        <CircularProgress color="inherit" />
        <Typography variant="h6" sx={{ mt: 2 }}>
          מבצע בדיקה חוזרת...
        </Typography>
      </Backdrop>
    </StyledContainer>
  );
};

export default CheckedInvoicesTable;